/**
 * BookingCardList — Card-based simple view for Booking list page.
 *
 * Travel-app inspired cards:
 *   - Left: FROM→TO time block with duration + visual connector
 *   - Center: resource/court name (hero) + slot position + customer info
 *   - Right: status badge + price + payment indicator
 *   - Footer: Check In / Check Out action buttons
 *
 * Grouped by exact date with optional relative badge (Today/Tomorrow).
 */

import { useMemo, useState, useCallback, useRef } from "react";
import { cn } from "@/lib/utils";
import {
  CalendarDays,
  Clock,
  User,
  Phone,
  Mail,
  StickyNote,
  CreditCard,
  Hash,
  LogIn,
  LogOut,
  CheckCircle2,
} from "lucide-react";
import { useLanguage } from "@/i18n/useLanguage";
import { getERPClient } from "@/lib/erp/client";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { SimpleTableStat } from "@/features/common/components/SimpleTableView";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type BookingRow = Record<string, unknown> & { name?: string };

interface BookingCardListProps {
  items: BookingRow[];
  isLoading: boolean;
  total: number;
  getRowId: (row: BookingRow) => string;
  onRowClick?: (id: string) => void;
  onRefresh?: () => void;
  summaryStats?: SimpleTableStat[];
}

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

const STATUS_STYLES: Record<string, { bg: string; text: string; dot: string }> = {
  Pending: {
    bg: "bg-amber-50 dark:bg-amber-900/20",
    text: "text-amber-700 dark:text-amber-400",
    dot: "bg-amber-500",
  },
  Confirmed: {
    bg: "bg-blue-50 dark:bg-blue-900/20",
    text: "text-blue-700 dark:text-blue-400",
    dot: "bg-blue-500",
  },
  Paid: {
    bg: "bg-emerald-50 dark:bg-emerald-900/20",
    text: "text-emerald-700 dark:text-emerald-400",
    dot: "bg-emerald-500",
  },
  Completed: {
    bg: "bg-neutral-100 dark:bg-neutral-800/40",
    text: "text-neutral-600 dark:text-neutral-400",
    dot: "bg-neutral-400",
  },
  Cancelled: {
    bg: "bg-red-50 dark:bg-red-900/20",
    text: "text-red-600 dark:text-red-400",
    dot: "bg-red-500",
  },
};

function BookingStatusBadge({ status }: { status: string }) {
  const style = STATUS_STYLES[status] ?? STATUS_STYLES.Pending;
  return (
    <span className={cn("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium", style.bg, style.text)}>
      <span className={cn("w-1.5 h-1.5 rounded-full", style.dot)} />
      {status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Summary stat bar colors
// ---------------------------------------------------------------------------

const STAT_BG: Record<string, string> = {
  default: "bg-neutral-50 dark:bg-neutral-800/60",
  green: "bg-emerald-50 dark:bg-emerald-900/20",
  orange: "bg-amber-50 dark:bg-amber-900/20",
  red: "bg-red-50 dark:bg-red-900/20",
  blue: "bg-blue-50 dark:bg-blue-900/20",
};
const STAT_DOT: Record<string, string> = {
  default: "bg-neutral-300 dark:bg-neutral-600",
  green: "bg-emerald-500 dark:bg-emerald-400",
  orange: "bg-amber-500 dark:bg-amber-400",
  red: "bg-red-500 dark:bg-red-400",
  blue: "bg-blue-500 dark:bg-blue-400",
};
const STAT_NUM: Record<string, string> = {
  default: "text-neutral-900 dark:text-neutral-100",
  green: "text-emerald-700 dark:text-emerald-400",
  orange: "text-amber-700 dark:text-amber-400",
  red: "text-red-700 dark:text-red-400",
  blue: "text-blue-700 dark:text-blue-400",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns exact date string + optional relative label */
function formatBookingDate(dateStr: string, th: boolean): { main: string; relative: string } {
  const d = new Date(dateStr + "T00:00:00");
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((target.getTime() - today.getTime()) / 86400000);

  const main = d.toLocaleDateString(th ? "th-TH" : "en-US", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  let relative = "";
  if (diffDays === 0) relative = th ? "วันนี้" : "Today";
  else if (diffDays === 1) relative = th ? "พรุ่งนี้" : "Tomorrow";
  else if (diffDays === -1) relative = th ? "เมื่อวาน" : "Yesterday";

  return { main, relative };
}

function formatTime(time: unknown): string {
  return String(time ?? "").slice(0, 5); // "10:00:00" -> "10:00"
}

/** Calculate duration between two HH:MM time strings */
function formatDuration(start: string, end: string): string {
  if (!start || !end) return "";
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  const totalMin = (eh * 60 + em) - (sh * 60 + sm);
  if (totalMin <= 0) return "";
  const hours = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h`;
  return `${mins}m`;
}

function formatCurrency(value: unknown): string {
  const num = Number(value ?? 0);
  if (num === 0) return "";
  return `\u0E3F${num.toLocaleString("th-TH", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

/** Format a datetime string to just HH:MM */
function formatDatetimeToTime(dt: unknown): string {
  if (!dt) return "";
  const s = String(dt);
  // "2026-06-22 10:30:00" → "10:30"
  const match = s.match(/(\d{2}:\d{2})/);
  return match ? match[1] : "";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BookingCardList({
  items,
  isLoading,
  total: _total,
  getRowId,
  onRowClick,
  onRefresh,
  summaryStats,
}: BookingCardListProps) {
  const { language } = useLanguage();
  const th = language === "th";
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    type: "in" | "out";
    bookingId: string;
    customerName: string;
    itemName: string;
    time: string;
  } | null>(null);

  // Refs to prevent stale closures in confirm handler
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  const requestCheckIn = useCallback((e: React.MouseEvent, bookingId: string, customerName: string, itemName: string, time: string) => {
    e.stopPropagation();
    setConfirmDialog({ type: "in", bookingId, customerName, itemName, time });
  }, []);

  const requestCheckOut = useCallback((e: React.MouseEvent, bookingId: string, customerName: string, itemName: string, time: string) => {
    e.stopPropagation();
    setConfirmDialog({ type: "out", bookingId, customerName, itemName, time });
  }, []);

  const executeAction = useCallback(async () => {
    if (!confirmDialog) return;
    const { type, bookingId } = confirmDialog;
    setConfirmDialog(null);
    setLoadingAction(bookingId + "-" + type);
    try {
      const client = getERPClient();
      const method = type === "in"
        ? "zaviago_backend.storefront.booking_admin.check_in_booking"
        : "zaviago_backend.storefront.booking_admin.check_out_booking";
      await client.call(method, { booking_id: bookingId });
      toast.success(type === "in" ? "Checked in" : "Checked out");
      onRefreshRef.current?.();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : `Check-${type} failed`;
      toast.error(msg);
    } finally {
      setLoadingAction(null);
    }
  }, [confirmDialog]);

  // Group bookings by date
  const groupedByDate = useMemo(() => {
    const groups: { date: string; label: string; relative: string; bookings: BookingRow[] }[] = [];
    const map = new Map<string, BookingRow[]>();

    for (const item of items) {
      const dateKey = String(item.booking_date ?? "");
      if (!map.has(dateKey)) map.set(dateKey, []);
      map.get(dateKey)!.push(item);
    }

    const sortedDates = [...map.keys()].sort();
    for (const date of sortedDates) {
      const formatted = date
        ? formatBookingDate(date, th)
        : { main: th ? "ไม่ระบุวันที่" : "No date", relative: "" };
      groups.push({
        date,
        label: formatted.main,
        relative: formatted.relative,
        bookings: map.get(date)!,
      });
    }
    return groups;
  }, [items, th]);

  // Loading skeleton
  if (isLoading && items.length === 0) {
    return (
      <div className="px-3 py-3 space-y-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-xl border border-neutral-200 dark:border-neutral-800 p-5 animate-pulse">
            <div className="flex gap-4">
              <div className="w-20 space-y-2 flex flex-col items-center">
                <div className="h-5 w-14 rounded bg-neutral-100 dark:bg-neutral-800" />
                <div className="w-px h-4 bg-neutral-100 dark:bg-neutral-800" />
                <div className="h-5 w-14 rounded bg-neutral-100 dark:bg-neutral-800" />
              </div>
              <div className="flex-1 space-y-2">
                <div className="h-4 w-40 rounded bg-neutral-100 dark:bg-neutral-800" />
                <div className="h-3 w-28 rounded bg-neutral-100 dark:bg-neutral-800" />
                <div className="h-3 w-32 rounded bg-neutral-100 dark:bg-neutral-800" />
              </div>
              <div className="h-5 w-20 rounded-full bg-neutral-100 dark:bg-neutral-800" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex items-center justify-center py-20 text-neutral-400 dark:text-neutral-500 text-[13px]">
        {th ? "ไม่พบรายการจอง" : "No bookings found"}
      </div>
    );
  }

  return (
    <>
    <div className="px-3 py-2">
      {/* Summary stats bar */}
      {summaryStats && summaryStats.length > 0 && (
        <div className="flex items-center gap-2 px-1 py-2 mb-2 flex-wrap">
          {summaryStats.map((stat, i) => {
            const c = stat.color ?? "default";
            return (
              <div key={i} className={cn("flex items-center gap-2 px-3 py-1.5 rounded-lg", STAT_BG[c])}>
                <span className={cn("w-1.5 h-1.5 rounded-full shrink-0", STAT_DOT[c])} />
                <span className={cn("text-[15px] font-semibold tabular-nums", STAT_NUM[c])}>{stat.value}</span>
                <span className="text-[12px] text-neutral-500 dark:text-neutral-400">{stat.label}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Booking cards grouped by date */}
      <div className="space-y-5">
        {groupedByDate.map((group) => (
          <div key={group.date}>
            {/* Date group header */}
            <div className="flex items-center gap-2 px-1 mb-2.5">
              <CalendarDays className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-[13px] font-medium text-foreground">{group.label}</span>
              {group.relative && (
                <span className="text-[11px] font-medium text-sky-600 dark:text-sky-400 bg-sky-50 dark:bg-sky-900/20 px-1.5 py-0.5 rounded">
                  {group.relative}
                </span>
              )}
              <span className="text-[11px] text-muted-foreground">
                ({group.bookings.length} {th ? "รายการ" : group.bookings.length === 1 ? "booking" : "bookings"})
              </span>
            </div>

            <div className="space-y-2.5">
              {group.bookings.map((booking) => {
                const rowId = getRowId(booking);
                const status = String(booking.status ?? "Pending");
                const itemName = String(booking.item_name ?? booking.item_code ?? "");
                const startTime = formatTime(booking.start_time);
                const endTime = formatTime(booking.end_time);
                const duration = formatDuration(startTime, endTime);
                const customerName = String(booking.customer_name ?? "");
                const customerPhone = String(booking.customer_phone ?? "");
                const customerEmail = String(booking.customer_email ?? "");
                const price = formatCurrency(booking.price);
                const notes = String(booking.notes ?? "");
                const bookingId = String(booking.name ?? "");
                const salesOrder = String(booking.sales_order ?? "");
                const slotNumber = Number(booking.booking_number ?? 0);
                const checkedInAt = String(booking.checked_in_at ?? "");
                const checkedOutAt = String(booking.checked_out_at ?? "");

                const isCheckedIn = !!checkedInAt;
                const isCheckedOut = !!checkedOutAt;
                const isCancelled = status === "Cancelled";
                const canCheckIn = !isCheckedIn && !isCancelled && status !== "Pending";
                const canCheckOut = isCheckedIn && !isCheckedOut;
                const isActionLoading = loadingAction === bookingId + "-in" || loadingAction === bookingId + "-out";

                return (
                  <div
                    key={rowId}
                    onClick={() => onRowClick?.(rowId)}
                    className={cn(
                      "rounded-xl border border-neutral-200 dark:border-neutral-800",
                      "bg-white dark:bg-neutral-950",
                      "transition-all duration-150",
                      onRowClick && "cursor-pointer hover:border-neutral-300 dark:hover:border-neutral-700 hover:shadow-sm",
                    )}
                  >
                    <div className="flex items-stretch">
                      {/* LEFT: Travel-app style FROM → TO time block */}
                      <div className="flex flex-col items-center justify-center px-4 py-4 border-r border-neutral-100 dark:border-neutral-800/60 min-w-[88px] gap-0.5">
                        <span className="text-[18px] font-bold tabular-nums text-foreground leading-none">
                          {startTime || "--:--"}
                        </span>

                        {endTime && (
                          <div className="flex flex-col items-center py-0.5">
                            <div className="w-px h-1.5 bg-neutral-300 dark:bg-neutral-600" />
                            {duration ? (
                              <span className="text-[10px] font-medium text-neutral-500 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 px-1.5 py-0.5 rounded my-0.5 tabular-nums">
                                {duration}
                              </span>
                            ) : (
                              <Clock className="w-3 h-3 text-neutral-400 my-0.5" />
                            )}
                            <div className="w-px h-1.5 bg-neutral-300 dark:bg-neutral-600" />
                          </div>
                        )}

                        {endTime && (
                          <span className="text-[18px] font-bold tabular-nums text-foreground leading-none">
                            {endTime}
                          </span>
                        )}
                      </div>

                      {/* CENTER + RIGHT: Main content */}
                      <div className="flex-1 flex flex-col min-w-0 py-3.5 px-4">
                        {/* Row 1: Resource/item name + Status + Price */}
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <h3 className="text-[15px] font-semibold text-foreground truncate leading-snug">
                              {itemName || (th ? "ไม่ระบุรายการ" : "No item")}
                            </h3>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-[11px] text-muted-foreground">
                                Booking ID: <span className="font-mono">{bookingId}</span>
                              </span>
                              {slotNumber > 0 && (
                                <>
                                  <span className="text-neutral-300 dark:text-neutral-700">|</span>
                                  <span className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground">
                                    <Hash className="w-3 h-3" />
                                    Slot {slotNumber}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1.5 shrink-0">
                            <BookingStatusBadge status={status} />
                            {price && (
                              <span className="text-[15px] font-semibold tabular-nums text-foreground">
                                {price}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Row 2: Customer info */}
                        <div className="flex items-center gap-4 mt-3 flex-wrap">
                          <div className="flex items-center gap-1.5">
                            <User className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
                            <span className="text-[13px] text-foreground/80">
                              {customerName || "Walk-in"}
                            </span>
                          </div>
                          {customerPhone && (
                            <div className="flex items-center gap-1.5">
                              <Phone className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
                              <span className="text-[13px] text-foreground/80">{customerPhone}</span>
                            </div>
                          )}
                          {customerEmail && (
                            <div className="flex items-center gap-1.5 min-w-0">
                              <Mail className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
                              <span className="text-[13px] text-foreground/80 truncate">{customerEmail}</span>
                            </div>
                          )}
                        </div>

                        {/* Row 3: Payment indicator + Notes */}
                        <div className="flex items-center gap-3 mt-2 flex-wrap">
                          {salesOrder && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-0.5 rounded">
                              <CreditCard className="w-3 h-3" />
                              {salesOrder}
                            </span>
                          )}
                          {notes && (
                            <div className="flex items-center gap-1.5 min-w-0">
                              <StickyNote className="w-3 h-3 text-neutral-400 shrink-0" />
                              <span className="text-[12px] text-muted-foreground line-clamp-1">{notes}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Footer: Check-in / Check-out actions */}
                    {!isCancelled && (
                      <div className="flex items-center justify-between px-4 py-2.5 border-t border-neutral-100 dark:border-neutral-800/60 bg-neutral-50/50 dark:bg-neutral-900/30 rounded-b-xl">
                        {/* Left: timestamps */}
                        <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                          {isCheckedIn && (
                            <span className="inline-flex items-center gap-1">
                              <LogIn className="w-3 h-3 text-emerald-500" />
                              In: {formatDatetimeToTime(checkedInAt)}
                            </span>
                          )}
                          {isCheckedOut && (
                            <span className="inline-flex items-center gap-1">
                              <LogOut className="w-3 h-3 text-neutral-400" />
                              Out: {formatDatetimeToTime(checkedOutAt)}
                            </span>
                          )}
                          {isCheckedIn && isCheckedOut && (
                            <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium">
                              <CheckCircle2 className="w-3 h-3" />
                              {th ? "เสร็จสิ้น" : "Completed"}
                            </span>
                          )}
                        </div>

                        {/* Right: action buttons */}
                        <div className="flex items-center gap-2">
                          {canCheckIn && (
                            <button
                              onClick={(e) => requestCheckIn(e, bookingId, customerName, itemName, `${startTime} - ${endTime}`)}
                              disabled={isActionLoading}
                              className={cn(
                                "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium",
                                "bg-primary text-primary-foreground hover:bg-primary/90",
                                "transition-colors duration-150",
                                isActionLoading && "opacity-50 cursor-not-allowed",
                              )}
                            >
                              <LogIn className="w-3.5 h-3.5" />
                              {loadingAction === bookingId + "-in" ? "..." : "Check In"}
                            </button>
                          )}
                          {canCheckOut && (
                            <button
                              onClick={(e) => requestCheckOut(e, bookingId, customerName, itemName, `${startTime} - ${endTime}`)}
                              disabled={isActionLoading}
                              className={cn(
                                "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium",
                                "bg-neutral-800 dark:bg-neutral-200 text-white dark:text-neutral-900 hover:bg-neutral-700 dark:hover:bg-neutral-300",
                                "transition-colors duration-150",
                                isActionLoading && "opacity-50 cursor-not-allowed",
                              )}
                            >
                              <LogOut className="w-3.5 h-3.5" />
                              {loadingAction === bookingId + "-out" ? "..." : "Check Out"}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>

      {/* Confirm check-in / check-out dialog */}
      <AlertDialog open={!!confirmDialog} onOpenChange={(open) => !open && setConfirmDialog(null)}>
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <div className={cn(
              "mx-auto flex h-12 w-12 items-center justify-center rounded-full mb-2",
              confirmDialog?.type === "in"
                ? "bg-sky-100 dark:bg-sky-900/30"
                : "bg-neutral-100 dark:bg-neutral-800",
            )}>
              {confirmDialog?.type === "in"
                ? <LogIn className="h-6 w-6 text-sky-600 dark:text-sky-400" />
                : <LogOut className="h-6 w-6 text-neutral-600 dark:text-neutral-400" />}
            </div>
            <AlertDialogTitle className="text-center">
              {confirmDialog?.type === "in"
                ? (th ? "ยืนยันเช็คอิน" : "Confirm Check In")
                : (th ? "ยืนยันเช็คเอาท์" : "Confirm Check Out")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center" asChild>
              <div className="space-y-3">
                <p className="text-muted-foreground text-sm">
                  {confirmDialog?.type === "in"
                    ? (th
                      ? `ยืนยันว่าลูกค้ามาถึงแล้ว ระบบจะบันทึกเวลาเช็คอิน`
                      : `Confirm that the customer has arrived. The system will record the check-in time.`)
                    : (th
                      ? `ยืนยันว่าลูกค้าออกแล้ว ระบบจะบันทึกเวลาเช็คเอาท์และเสร็จสิ้นการจอง`
                      : `Confirm that the customer has left. The system will record the check-out time and complete the booking.`)}
                </p>
                <div className="rounded-lg border bg-muted/30 p-3 text-left space-y-1.5">
                  {confirmDialog?.customerName && (
                    <div className="flex items-center gap-2 text-sm">
                      <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="font-medium">{confirmDialog.customerName}</span>
                    </div>
                  )}
                  {confirmDialog?.itemName && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CalendarDays className="h-3.5 w-3.5 shrink-0" />
                      <span>{confirmDialog.itemName}</span>
                    </div>
                  )}
                  {confirmDialog?.time && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="h-3.5 w-3.5 shrink-0" />
                      <span>{confirmDialog.time}</span>
                    </div>
                  )}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:justify-center gap-2 mt-1">
            <AlertDialogCancel className="flex-1">
              {th ? "ยกเลิก" : "Cancel"}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={executeAction}
              className={cn(
                "flex-1",
                confirmDialog?.type === "in"
                  ? "bg-primary hover:bg-primary/90"
                  : "bg-neutral-800 hover:bg-neutral-700 dark:bg-neutral-200 dark:hover:bg-neutral-300 dark:text-neutral-900",
              )}
            >
              {confirmDialog?.type === "in"
                ? (th ? "เช็คอิน" : "Check In")
                : (th ? "เช็คเอาท์" : "Check Out")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Summary stats helper (called from StandardListPage)
// ---------------------------------------------------------------------------

export function computeBookingSummaryStats(items: Record<string, unknown>[]): SimpleTableStat[] {
  let pending = 0;
  let confirmed = 0;
  let paid = 0;
  let cancelled = 0;
  for (const row of items) {
    const status = String(row.status ?? "");
    if (status === "Pending") pending++;
    else if (status === "Confirmed") confirmed++;
    else if (status === "Paid") paid++;
    else if (status === "Cancelled") cancelled++;
  }
  const stats: SimpleTableStat[] = [];
  if (pending > 0) stats.push({ label: "Pending", value: pending, color: "orange" });
  if (confirmed > 0) stats.push({ label: "Confirmed", value: confirmed, color: "blue" as "default" });
  if (paid > 0) stats.push({ label: "Paid", value: paid, color: "green" });
  if (cancelled > 0) stats.push({ label: "Cancelled", value: cancelled, color: "red" });
  return stats;
}
